import React, { useState, useEffect } from 'react';
import { Typography, Space } from 'antd';
import { ClockCircleOutlined, LockOutlined } from '@ant-design/icons';
import { useSecurity } from '../../hooks/useSecurity';
import { useI18n } from '../../hooks/useI18n';
import './SecurityTimer.scss';

const { Text } = Typography;

const SecurityTimer = ({ position = 'header' }) => {
	const { isEnabled, inactivityTimeout, timeRemaining } = useSecurity();
	const { t } = useI18n();
	const [shouldBlink, setShouldBlink] = useState(false);

	// Check if timer should blink (below 30 seconds)
	useEffect(() => {
		if (timeRemaining !== null && timeRemaining <= 30 && timeRemaining > 0) {
			setShouldBlink(true);
		} else {
			setShouldBlink(false);
		}
	}, [timeRemaining]);

	// Don't show if security is not enabled, no time remaining, or time is above 30 seconds
	if (!isEnabled || timeRemaining === null || timeRemaining <= 0 || timeRemaining > 30) {
		return null;
	}

	const formatTime = (seconds) => {
		if (seconds < 60) {
			return t('security.timer_seconds', { seconds });
		}
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		return remainingSeconds > 0 
			? t('security.timer_minutes', { minutes, seconds: remainingSeconds })
			: t('security.timer_minutes', { minutes, seconds: 0 }).replace(' 0s', '');
	};

	const getTimerColor = () => {
		if (timeRemaining <= 10) return '#ff4d4f'; // Red for critical
		if (timeRemaining <= 30) return '#faad14'; // Orange for warning
		return '#52c41a'; // Green for normal
	};

	const getTimerIcon = () => {
		if (timeRemaining <= 30) {
			return <LockOutlined style={{ color: getTimerColor() }} />;
		}
		return <ClockCircleOutlined style={{ color: getTimerColor() }} />;
	};

	return (
		<div className={`security-timer ${position} ${shouldBlink ? 'blink' : ''}`}>
			<Space size="small" align="center">
				{getTimerIcon()}
				<Text 
					style={{ 
						color: getTimerColor(),
						fontWeight: timeRemaining <= 30 ? 'bold' : 'normal',
						fontSize: position === 'header' ? '12px' : '14px'
					}}
				>
					{position === 'header' 
						? formatTime(timeRemaining)
						: `${t('security.timer_remaining')} ${formatTime(timeRemaining)}`
					}
					{timeRemaining <= 10 && position !== 'header' && (
						<div style={{ fontSize: '11px', color: getTimerColor(), marginTop: '2px' }}>
							{t('security.timer_critical')}
						</div>
					)}
				</Text>
			</Space>
		</div>
	);
};

export default SecurityTimer;